import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAccess } from '../../auth/AccessContext.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';
import AnalyticsFeeds from './AnalyticsFeeds.jsx';

/**
 * The Analytics Feeds page (design 9): a module of its own in the navigation
 * since the owner moved it out of the business-day drawer (9 Oct 2026). One
 * row a business date - every date not DONE, oldest first, then the latest
 * DONE ones (GET /books/feeds?attention=1) - with its files, Re-send and
 * Confirm no WM holdings. Exported for the SSR tests, which hand the read in
 * (effects do not run in a server render).
 *
 * @param {{ feeds: { days: object[] }|null, error?: { message: string }|null, onChanged?: () => void }} props
 */
export const AnalyticsFeedsPage = ({ feeds, error = null, onChanged }) => (
    <div className="workspace analytics-feeds-workspace">
        <div className="workspace-body">
            <section className="panel">
                <h3 className="panel-title">Analytics Feeds</h3>
                <div className="permission-info-banner analytics-feeds-info">
                    <ActionIcon name="info" />
                    <span>
                        The daily files the Core sends to analytics - transactions, unit prices and client holdings - one row a
                        business date once it has closed, oldest first. A date that needs you says why.
                    </span>
                </div>
                <AnalyticsFeeds days={feeds?.days ?? null} error={error} onChanged={onChanged} />
            </section>
        </div>
    </div>
);

/**
 * The module: reads the feeds once on opening, again after a re-send or a waiver, and whenever a hint says
 * they or the business day changed (live screens, IP-162: `feeds` from FEEDS_RESEND and FEEDS_WM_WAIVE, from
 * the sweeper after every change it makes (feedsStep, feedsBuild) and from analytics' reports
 * (feedsAnalyticsEvent); `businessDay` when a day closes and a new date reaches the feeds).
 */
const AnalyticsFeedsTab = () => {
    const { api } = useAccess();
    const [feeds, setFeeds] = useState(null);
    const [error, setError] = useState(null);
    // Each read takes the next number; only the newest one's answer or error lands. The hub and onChanged call
    // load() and drop what it returns, so an older, slower read could otherwise overwrite a newer one.
    const seq = useRef(0);

    const load = useCallback(() => {
        const id = ++seq.current;
        api.books.feeds({ attention: true }).then(
            (answer) => {
                if (id !== seq.current) return;
                setFeeds(answer);
                setError(null);
            },
            (e) => {
                if (id === seq.current) setError(e);
            }
        );
    }, [api]);

    useEffect(() => {
        load();
        // An unmount: nothing still in flight lands after it.
        return () => {
            seq.current += 1;
        };
    }, [load]);
    useLiveTopic(['feeds', 'businessDay'], load);

    return <AnalyticsFeedsPage feeds={feeds} error={error} onChanged={load} />;
};

export default AnalyticsFeedsTab;
